import { invoke } from '@tauri-apps/api/core'

import { logService, LOG_CATEGORIES } from '@/services/logService'
import { getActiveOfflineOwnerUserId } from '@/services/offlineAuthBridge'
import { isTauri } from '@/services/platformService'
import { useOfflineStore } from '@/stores/offlineStore'

const blobUrlCache = new Map<string, string>()
const MAX_CACHE = 100

function cacheKey(
  ownerUserId: number,
  workspaceId: string,
  photoId: string,
  variant: string,
): string {
  return `${ownerUserId}:${workspaceId}:${photoId}:${variant}`
}

function pruneCache(): void {
  if (blobUrlCache.size >= MAX_CACHE) {
    const firstKey = blobUrlCache.keys().next().value
    if (firstKey !== undefined) {
      const url = blobUrlCache.get(firstKey)
      if (url) URL.revokeObjectURL(url)
      blobUrlCache.delete(firstKey)
    }
  }
}

export async function resolvePhotoUrl(
  workspaceId: string,
  photoId: string,
  url: string,
  variant: 'full' | 'thumb' = 'full',
): Promise<string> {
  if (!isTauri()) return url

  const offlineStore = useOfflineStore()
  const entry = offlineStore.getEntry(workspaceId)
  if (!entry || !entry.photosIncluded) return url
  const expectedOwnerUserId = getActiveOfflineOwnerUserId()
  if (expectedOwnerUserId === null) return url

  const key = cacheKey(expectedOwnerUserId, workspaceId, photoId, variant)
  const cached = blobUrlCache.get(key)
  if (cached) return cached

  try {
    const result = await invoke<{ bytes: number[]; content_type: string }>('read_offline_photo', {
      workspaceId,
      photoId,
      variant,
    })
    if (getActiveOfflineOwnerUserId() !== expectedOwnerUserId) return ''

    const blob = new Blob([new Uint8Array(result.bytes)], { type: result.content_type })
    pruneCache()
    const blobUrl = URL.createObjectURL(blob)
    blobUrlCache.set(key, blobUrl)
    return blobUrl
  } catch (error) {
    if (getActiveOfflineOwnerUserId() !== expectedOwnerUserId) return ''
    logService.debug(LOG_CATEGORIES.OFFLINE, 'Falling back to online photo URL', {
      workspaceId,
      photoId,
      variant,
      error,
    })
    return url
  }
}
